import React, { useEffect, useState } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import "./../../CompanysForm.css"
import { clearCNPJ } from "helpers/clearCepAndCNPJValues"
import { post, put } from "helpers/api_helper"
import Grid from "components/Common/Grid"

// Função para aplicar máscara de CNPJ
const formatCNPJ = (value) => {
  const numbers = value.replace(/\D/g, '')
  return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
}

const validationSchema = Yup.object({
  Classificacao: Yup.number()
    .integer("Quantidade de débitos deve ser um número inteiro")
    .max(32767, "Valor aceito até: 32767")
    .optional(),
  CnpjBase: Yup.string()
    .matches(/^\d{8,14}$/, "CNPJ Base deve conter entre 8 e 14 números.")
    .required("CNPJ é obrigatório."),
  RazaoSocial: Yup.string().optional(),
  QtdeDebitos: Yup.number()
    .integer("Quantidade de débitos deve ser um número inteiro")
    .optional(),
  Valor: Yup.string()
    .matches(
      /^[0-9]+(,[0-9]{1,2})?$/,
      "Valor deve ser um número com vírgula para separação decimal (ex: 124,50)"
    )
    .optional(),
  PalavraChave: Yup.string().optional(),
})

const DebtorForm = ({
  companySelected,
  setCompanySelected,
  setShowList,
  setCurrentPage,
}) => {
  const [isLoadingCNPJ, setIsLoadingCNPJ] = useState(false)
  const initialValues = {
    Classificacao:
      companySelected !== null && companySelected.Classificacao !== null
        ? companySelected.Classificacao
        : "",
    CnpjBase:
      companySelected !== null && companySelected.CnpjBase !== null
        ? companySelected.CnpjBase
        : "",
    RazaoSocial:
      companySelected !== null && companySelected.RazaoSocial !== null
        ? companySelected.RazaoSocial
        : "",
    QtdeDebitos:
      companySelected !== null && companySelected.QtdeDebitos !== null
        ? companySelected.QtdeDebitos
        : "",
    Valor:
      companySelected !== null && companySelected.Valor !== null
        ? companySelected.Valor
        : "",
    PalavraChave:
      companySelected !== null && companySelected.PalavraChave !== null
        ? companySelected.PalavraChave
        : "",
  }
  const formik = useFormik({
    validationSchema,
    initialValues,
    onSubmit: values => {
      console.log("Formulário enviado com os seguintes valores:", values)
      // Garantir que apenas os 8 primeiros dígitos sejam salvos
      const cnpjNumbers = values.CnpjBase.replace(/\D/g, '')
      values.CnpjBase = cnpjNumbers.substring(0, 8)
      values.SqlLeadsPje = `OR (((Base_site.[Polo passivo]) Like '*${values.RazaoSocial}*'))`
      values.LotePje = "Lote 1"
      if (companySelected === null) {
        handleCreateNewCompany(values)
      } else {
        handleEditCompany(values)
      }
    },
  })

  const handleCreateNewCompany = async payload => {
    const url =
      `${process.env.REACT_APP_API_INT_LEADS}/api/empresas-devedoras`

    try {
      const response = await post(url, payload)
      console.log(response)
      alert("A empresa foi cadastrada com sucesso!")
      setShowList(true)
      setCurrentPage(1)
      return
    } catch (error) {
      if (error.response?.status === 400) {
        alert(error.response.data.message)
        return
      } else {
        alert(
          "Algo de errado aconteceu! Tente mais tarde ou verifique sua internet"
        )
        return
      }
    }
  }

  const handleEditCompany = async payload => {
    const url = `${process.env.REACT_APP_API_INT_LEADS}/api/empresas-devedoras/${companySelected.Id}`

    try {
      const response = await put(url, payload)
      console.log(response)
      alert("A empresa foi editada com sucesso!")
      handleCancelEdditing()
      setCurrentPage(1)

      return
    } catch (error) {
      if (error.response?.status === 400) {
        alert(error.response.data.message)
        return
      } else {
        alert(
          "Algo de errado aconteceu! Tente mais tarde ou verifique sua internet"
        )
        return
      }
    }
  }

  const handleCancelEdditing = () => {
    setCompanySelected(null)
    setShowList(true)
  }

  // Função para buscar dados do CNPJ na API BrasilAPI
  const searchCNPJ = async cnpj => {
    setIsLoadingCNPJ(true)
    try {
      const response = await fetch(
        `https://brasilapi.com.br/api/cnpj/v1/${cnpj}`
      )
      const data = await response.json()
      if (data && !data.message) {
        // Extrair os 8 primeiros dígitos para CnpjBase
        const cnpjBase = cnpj.substring(0, 8)
        formik.setValues({
          ...formik.values,
          CnpjBase: cnpjBase,
          RazaoSocial: data.razao_social || "",
        })
      } else {
        alert(data.message || "CNPJ não encontrado")
      }
    } catch (error) {
      alert("Erro ao buscar CNPJ. Verifique sua conexão.")
    } finally {
      setIsLoadingCNPJ(false)
    }
  }

  useEffect(() => {
    !companySelected && formik.resetForm({ values: initialValues })
  }, [companySelected])

  // useEffect para autopreenchimento quando CNPJ completo é inserido no campo CnpjBase
  useEffect(() => {
    if (formik.values.CnpjBase) {
      const cnpjNumbers = formik.values.CnpjBase.replace(/\D/g, '')
      
      // Se o usuário digitar um CNPJ completo (14 dígitos), buscar dados
      if (cnpjNumbers.length === 14) {
        searchCNPJ(cnpjNumbers)
      }
    }
  }, [formik.values.CnpjBase])

  return (
    <div className="companys-form-container">
      <h1 className="h1-form-title">Preencha o formulário abaixo:</h1>
      <span className="span-form-subtitle">Campos obrigatórios *</span>
      {companySelected !== null && (
        <span className="span-form-edditing">
          Atenção! Você está editando a empresa selecionada!
        </span>
      )}
      <form className="form-companys-container" onSubmit={formik.handleSubmit}>
        <Grid>
          <div className="input-and-label">
            <label className="company-label">CNPJ Base*</label>
            <input
              className="company-input-field"
              type="text"
              name="CnpjBase"
              placeholder="Digite 8 dígitos ou CNPJ completo para buscar"
              value={formik.values.CnpjBase}
              onChange={(e) => {
                const numbers = e.target.value.replace(/\D/g, '')
                // Permitir até 14 dígitos para buscar CNPJ completo, mas salvar apenas 8
                formik.setFieldValue('CnpjBase', numbers)
              }}
              onBlur={formik.handleBlur}
              maxLength={18}
            />
            {isLoadingCNPJ && (
              <span className="span-form-loading">Buscando dados...</span>
            )}
            {formik.touched.CnpjBase && formik.errors.CnpjBase ? (
              <span className="span-form-error-companys">
                {formik.errors.CnpjBase}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Classificação</label>
            <input
              className="company-input-field"
              type="number"
              name="Classificacao"
              value={formik.values.Classificacao}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.Classificacao && formik.errors.Classificacao ? (
              <span className="span-form-error-companys">
                {formik.errors.Classificacao}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Razão Social</label>
            <input
              className="company-input-field"
              type="text"
              name="RazaoSocial"
              value={formik.values.RazaoSocial}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.RazaoSocial && formik.errors.RazaoSocial ? (
              <span className="span-form-error-companys">
                {formik.errors.RazaoSocial}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Quantidade de débitos</label>
            <input
              className="company-input-field"
              type="number"
              name="QtdeDebitos"
              value={formik.values.QtdeDebitos}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.QtdeDebitos && formik.errors.QtdeDebitos ? (
              <span className="span-form-error-companys">
                {formik.errors.QtdeDebitos}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Valor R$</label>
            <input
              className="company-input-field"
              type="text"
              name="Valor"
              value={formik.values.Valor}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.Valor && formik.errors.Valor ? (
              <span className="span-form-error-companys">
                {formik.errors.Valor}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Palavra chave</label>
            <input
              className="company-input-field"
              type="text"
              name="PalavraChave"
              value={formik.values.PalavraChave}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.PalavraChave && formik.errors.PalavraChave ? (
              <span className="span-form-error-companys">
                {formik.errors.PalavraChave}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Status</label>
            <select
              className="company-input-field"
              name="status"
              value={formik.values.status}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
            {formik.touched.status && formik.errors.status ? (
              <span className="span-form-error-companys">
                {formik.errors.status}
              </span>
            ) : null}
          </div>
        </Grid>

        <Grid marginTop={32}>
          <button
            className="btn btn-primary w-md "
            type="submit"
            style={{ height: 40, border: "none" }}
          >
            Enviar
          </button>
          {companySelected !== null && (
            <button
              className="btn btn-primary w-md "
              type="button"
              style={{ backgroundColor: "red", height: 40, border: "none" }}
              onClick={handleCancelEdditing}
            >
              Cancelar
            </button>
          )}
        </Grid>
      </form>
    </div>
  )
}

export default DebtorForm
